import { type ReactNode, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { sessionQueryKey } from '~/lib/queries/session';
import {
  currentOrganizationQueryOptions,
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
  myInvitationsQueryOptions,
  useCreateOrganization,
} from '~/lib/queries/organization';
import { PORTFOLIO_KEY } from '~/features/portfolio/use-portfolio';
import { resolveLandingRoute } from '~/lib/landing';
import { markOnboardingComplete } from '~/lib/stores/onboarding-celebration';
import { Stack, TextInput, Button, Reveal } from '~/components/ui';
import { Building2 } from '~/components/icons';
import { OnboardingHeader } from '~/components/onboarding/OnboardingHeader';
import styles from '~/components/onboarding/onboarding-step.module.css';

const ORG_NAME_MAX = 100;

export const Route = createFileRoute('/_authed/onboarding/create-organization')({
  beforeLoad: async ({ context }) => {
    // An invited teammate is auto-accepted into their inviter's org at
    // onboarding-complete, so by the time they reach this step they already have
    // an org — forward them straight to the dashboard rather than asking them to
    // create a second one. Resolved server-side, so they never flash this form.
    const { organization } = await context.queryClient.ensureQueryData(
      currentOrganizationQueryOptions(),
    );
    if (organization) {
      throw redirect({ to: await resolveLandingRoute(context.queryClient) });
    }
    // An org-less user with PENDING INVITATIONS chooses those first (explicit
    // accept/decline on the invitations step) — otherwise an invited teammate
    // would be funnelled into creating a duplicate solo org.
    const { invitations } = await context.queryClient.ensureQueryData(
      myInvitationsQueryOptions(),
    );
    if (invitations.length > 0) {
      throw redirect({ to: '/onboarding/invitations' });
    }
  },
  component: CreateOrganizationRoute,
});

function CreateOrganizationRoute(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createOrganization = useCreateOrganization();

  const [name, setName] = useState('');
  const [error, setError] = useState('');

  async function submitAsync(): Promise<void> {
    triggerHaptic();
    setError('');
    const trimmed = name.trim();
    if (trimmed === '') return;

    try {
      await createOrganization.mutateAsync({ body: { name: trimmed } });
      // REMOVE the session + current-org caches (not just invalidate) so the
      // `_protected` gate — which reads them via `ensureQueryData` with no active
      // observer to trigger a refetch — resolves the freshly-created org instead of
      // the stale "no org" value and bounces back to onboarding. Drop any prior
      // org's portfolio cache too so the landing decision reads the new (empty)
      // org's loan count fresh.
      queryClient.removeQueries({ queryKey: sessionQueryKey });
      queryClient.removeQueries({ queryKey: [...currentOrganizationQueryKey] });
      queryClient.removeQueries({ queryKey: [...myOrganizationsQueryKey] });
      queryClient.removeQueries({ queryKey: [...PORTFOLIO_KEY] });
      // Onboarding is complete — raise the "You're all set" celebration for the
      // landing surface. A brand-new org has no loans, so this lands on /uploads.
      markOnboardingComplete();
      void navigate({ to: await resolveLandingRoute(queryClient), replace: true });
      // Leave the button in its loading state — the navigation unmounts this form.
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: 'onboarding create org' });
      setError(t('onboarding.createOrg.error'));
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submitAsync();
      }}
      noValidate
      data-testid="create-org-step"
    >
      <Reveal variant="fade">
        <Stack gap="lg">
          <OnboardingHeader
            mark={
              <div className={styles.medallion}>
                <Building2 size={32} weight="light" />
              </div>
            }
            title={t('onboarding.createOrg.title')}
            subtitle={t('onboarding.createOrg.subtitle')}
          />

          <TextInput
            label={t('onboarding.createOrg.nameLabel')}
            description={t('onboarding.createOrg.hint')}
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
              if (error) setError('');
            }}
            error={error}
            placeholder={t('onboarding.createOrg.namePlaceholder')}
            maxLength={ORG_NAME_MAX}
            required
            autoFocus
            data-testid="create-org-name"
          />

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={createOrganization.isPending}
            disabled={name.trim() === ''}
            data-testid="create-org-submit"
          >
            {t('onboarding.createOrg.submit')}
          </Button>
        </Stack>
      </Reveal>
    </form>
  );
}
